// src/contexts/AuthContext.js
// 웹 필드(nickname, points, isAdmin) 그대로 사용
// 앱 UI에서 필요한 displayName, point는 getter로 제공
// 46차: console 전수 제거
// 74차: authRedirectSuppressed 추가 — AppStack이 isLoggedIn=true가 되는 즉시 Main으로
//       자동 이동시키는데, 회원가입 완료 화면(1.5초 축하 표시) 같이 로그인 직후 잠깐
//       다른 화면을 보여줘야 하는 흐름과 경쟁해 "Main이 먼저 올라와버리고 완료 화면은
//       스택에 남아 계속 로딩 상태로 있는" 문제가 있었음. 이 플래그가 true인 동안은
//       AppStack의 자동 이동을 보류 — 기본값 false라 기존 로그인/관리자 흐름엔 영향 없음.
// 75차: pendingAdmins 조회 후 결과를 그냥 버리던 죽은 코드 제거 — 실제 병합은
//       functions/index.js의 onNewUserDoc(users onCreate 트리거)이 서버에서 처리하도록
//       이관. 병합 직후에도 여기서 getUserProfile을 호출하므로 반영은 그대로 됨.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import { getUserProfile } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null); // 웹 필드 구조 그대로
  const [loading, setLoading] = useState(true);
  // 74차: 로그인 직후 자동 Main 이동을 잠깐 보류하고 싶은 화면(회원가입 완료 등)이 켜는 플래그
  const [authRedirectSuppressed, setAuthRedirectSuppressed] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        try {
          const data = await getUserProfile(firebaseUser.uid);
          setProfile(data || null);
        } catch {
          setProfile(null);
        }

      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // 78차 56차: 클로저로 캡처한 state의 user 대신 auth.currentUser(항상 최신을 반환하는
  // Firebase SDK 동기 getter)를 직접 참조 — RegisterScreen.js의 handleRegister가 버튼을
  // 누른 시점(아직 로그인 전, user가 null이던 렌더)에 이 함수의 클로저를 이미 붙잡고
  // 있어서, registerWithEmail() 완료 뒤 이 함수를 호출해도 그 순간의 오래된 클로저 안
  // user는 여전히 null이라 매번 조용히 아무 것도 안 하고 있었음(실기기 테스트로 발견 —
  // 78차55차의 "회원가입 직후 refreshProfile() 호출 추가" 수정이 사실상 무효였던 원인).
  const refreshProfile = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    try {
      const data = await getUserProfile(currentUser.uid);
      setProfile(data || null);
    } catch { /* 무음 처리 */ }
  };

  // ── 웹 필드를 앱 UI에서 쓰기 편하게 getter로 제공 ──────────
  // profile.nickname  → displayName
  // profile.points    → point
  // profile.isAdmin   → isAdmin
  // profile.adminRole → adminRole

  const displayName = profile?.nickname || user?.displayName || '';
  const point = profile?.points ?? 0;
  const adminRole = profile?.adminRole ?? null;
  const isAdmin = profile?.isAdmin === true || adminRole === 'super';
  const isStoreAdmin = adminRole === 'store';

  const value = {
    user,
    profile,          // 웹 필드 구조 원본 (nickname, points, ...)
    loading,
    isLoggedIn: !!user,

    // 앱 UI용 편의 getter
    displayName,      // profile.nickname 우선
    point,            // profile.points
    adminRole,
    isAdmin,          // super 또는 isAdmin=true
    isStoreAdmin,

    refreshProfile,
    authRedirectSuppressed,
    setAuthRedirectSuppressed,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth는 AuthProvider 내부에서 사용해야 합니다.');
  return ctx;
};